import {
  Box,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
  Container,
  CircularProgress,
  Skeleton,
  Card,
  CardContent,
} from "@mui/material";
import colors from "colors/color";
import React, { useEffect, useState } from "react";
import JobCard from "views/Landing/JobCard";
import logo from "../../assets/img/new_logo.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomPagination from "views/client/pagination";
import Details from "./Details";
import axios from "axios";
import { decryptId } from "../../utils/encryption";

// Add JobCardSkeleton component
const JobCardSkeleton = () => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
      transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
      // "&:hover": {
      //   transform: "translateY(-4px)",
      //   boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
      // },
    }}
  >
    <CardContent sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
      {/* Company Logo and Name */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
        </Box>
      </Box>

      {/* Job Title */}
      <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />

      {/* Job Details */}
      <Box sx={{ mb: 2 }}>
        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="50%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="60%" height={20} />
      </Box>

      {/* Tags */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Skeleton variant="rounded" width={80} height={24} />
        <Skeleton variant="rounded" width={100} height={24} />
        <Skeleton variant="rounded" width={70} height={24} />
      </Box>

      {/* Salary and Location */}
      {/* <Box sx={{ mt: "auto", pt: 2, borderTop: "1px solid #eee" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Skeleton variant="text" width="40%" height={20} />
          <Skeleton variant="text" width="30%" height={20} />
        </Box>
        <Skeleton variant="text" width="50%" height={20} />
      </Box> */}
    </CardContent>
  </Card>
);

function ListPosts({ onJobClick, setSelectedJob, setOpenDialog, posts,
  loading,
  error,
  pagination,
  goToNextPage,
  goToPreviousPage,
  goToPage,
  refreshPosts,
  spontaneous,
  fromSavedPosts,
  selectedJobs,
  onToggleSelect,
  isJobSelected,
}) {
  const { jobId: encryptedJobId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const jobIdFromUrl = searchParams.get('jobId');
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [isLoadingSharedPost, setIsLoadingSharedPost] = useState(false);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiUrlsoket = process.env.REACT_APP_API_URL_SOKET;

  const [ChatComponent, setChatComponent] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [noResultsMessage, setNoResultsMessage] = useState("");
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [lastPost, setLastPost] = useState(null);
  const [firstJob, setFirstJob] = useState(null);
  const [isShrinked, setIsShrinked] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 892px)");
  const navigate = useNavigate();

  const [incomingPost, setIncomingPost] = useState(null);
  const breakpoints = {
    sm: '(min-width: 640px)',
    md: '(min-width: 1304px)',
    lg: '(min-width: 1688px)',
  };

  const handleChangePage = (event, newPage) => {
    goToPage(newPage + 1);
    window.scrollTo({
      top: 500,
      behavior: "smooth",
    });
  };

  const handleSearchResults = (results, message) => {
    setSearchResults(results);
    setNoResultsMessage(message);
    if (results.length > 0) {
      setSelectedJob(results[0].post);
    }
  };

  const fetchSharedPost = async (id) => {
    try {
      setIsLoadingSharedPost(true);
      const response = await axios.get(`${apiUrl}posts/${id}/find`);
      if (response.data) {
        setSelectedJobDetails(response.data);
        setSelectedJob(response.data);
        setOpenDialog(true);
      }
    } catch (error) {
      console.error(t('errors.error_fetching_data'), error);
    } finally {
      setIsLoadingSharedPost(false);
    }
  };

  useEffect(() => {
    if (encryptedJobId) {
      // Decrypt the job ID
      const decryptedId = decryptId(encryptedJobId);
      if (!decryptedId) {
        console.error(t('errors.error_fetching_data'));
        return;
      }

      // First try to find the job in the current posts array
      const job = posts.find(post => post.id === decryptedId);
      if (job) {
        setSelectedJobDetails(job);
        setSelectedJob(job);
        setOpenDialog(true);
      } else {
        // If not found in current posts, fetch it from the API
        fetchSharedPost(decryptedId);
      }
    }
  }, [encryptedJobId, posts, setSelectedJob, setOpenDialog]);

  return (
    <Container maxWidth="xxl">
      {isLoadingSharedPost ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : selectedJobDetails ? (
        <Details
          job={selectedJobDetails}
          onClose={() => {
            setSelectedJobDetails(null);
            setSelectedJob(null);
            setOpenDialog(false);
            navigate('/jobs');
          }}
          posts={posts}
          refresh={refreshPosts}
          setSelectedJob={setSelectedJob}
          setOpenDialog={setOpenDialog}
        />
      ) : (
        <Box
          sx={{
            marginTop: { xs: 2, sm: 3, md: 4 },
            padding: { xs: 0, sm: 2, md: 0 },
          }}
        >
          {!fromSavedPosts && (
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: { xs: "28px", sm: "34px", md: "40px" },
                lineHeight: { xs: "42px", sm: "50px", md: "60px" },
                letterSpacing: "0%",
                color: colors.primary,
                display: "flex",
                flexDirection: "row",
                alignItems: { xs: "flex-start", sm: "center" },
              }}
            >
              {loading ? (
                <Skeleton width={100} height={40} />
              ) : (
                <>
                  {pagination?.totalItems || 0}
                  <Box
                    sx={{
                      color: "#000",
                      ml: { xs: 0, sm: 1 },
                      fontWeight: 400,
                    }}
                  >
                    {t('postPage.positions_found')}
                  </Box>
                </>
              )}
            </Typography>
          )}
          <Grid
            container
            spacing={{ xs: 2, sm: 2, md: 3, lg: 3 }}
            sx={{ marginTop: { xs: 2, sm: 3, md: 4 } }}
          >
            {loading ? (
              // Show skeletons while loading
              [...Array(8)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={`skeleton-${index}`}>
                  <JobCardSkeleton />
                </Grid>
              ))
            ) : posts.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: { xs: "28px", sm: "34px", md: "40px" },
                    lineHeight: { xs: "42px", sm: "50px", md: "60px" },
                    letterSpacing: "0%",
                    color: colors.primary,
                    display: "flex",
                  }}>
                    {t('postPage.noPostsFound')}
                  </Typography>
                  <Typography sx={{
                    mt: 1,
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: { xs: "16px", sm: "18px", md: "20px" },
                    lineHeight: { xs: "24px", sm: "28px", md: "32px" },
                    letterSpacing: "0%",
                    color: "#00000080",
                  }}>
                    {t('postPage.noPostsDescription')}
                  </Typography>
                </Box>
              </Grid>
            ) : (
              // Show actual job cards when loaded
              posts.map((job) => (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={job.id}>
                  <JobCard
                    job={job}
                    setSelectedJob={setSelectedJob}
                    setOpenDialog={setOpenDialog}
                    spontaneous={spontaneous}
                    fromSavedPosts={fromSavedPosts}
                    refresh={refreshPosts}
                    isSelected={isJobSelected ? isJobSelected(job.id) : false}
                    onToggleSelect={onToggleSelect}
                  />
                </Grid>
              ))
            )}
          </Grid>
          {pagination && pagination.totalPages > 0 && posts.length > 0 && (
            <div
              className="pagination-container"
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <CustomPagination
                  page={pagination.currentPage - 1}
                  rowsPerPage={10}
                  count={pagination.totalItems}
                  handleChangePage={(event, newPage) => {
                    if (newPage + 1 <= pagination.totalPages) {
                      goToPage(newPage + 1);
                      window.scrollTo({
                        top: 500,
                        behavior: "smooth",
                      });
                    }
                  }}
                  showFirstButton={pagination.currentPage > 2}
                  showLastButton={pagination.currentPage < pagination.totalPages - 1}
                  siblingCount={1}
                  boundaryCount={1}
                  disabled={loading}
                  showNextButton={pagination.hasNextPage}
                  showPreviousButton={pagination.hasPreviousPage}
                />
              )}
            </div>
          )}
        </Box>
      )}
    </Container>
  );
}

export default ListPosts;
